import {
    RepositorySelectorActions
} from "DistributedTaskControls/Actions/RepositorySelectorActions";
import { ActionsHubManager } from "DistributedTaskControls/Common/Actions/ActionsHubManager";
import { ActionCreatorBase } from "DistributedTaskControls/Common/Actions/Base";
import * as DTCResources from "DistributedTaskControls/Resources/TFS.Resources.DistributedTaskControls";
import {
    IRepositorySelectorProps
} from "DistributedTaskControls/SharedControls/InputControls/Components/RepositorySelectorComponent";
import {
    ActionCreatorKeys
} from "DistributedTaskControls/SharedControls/InputControls/RepositoryPickerConstants";
import {
    LoadableComponentActionsHub
} from "DistributedTaskControls/SharedControls/LoadableComponent/LoadableComponentActionsHub";
import { RepositoryPickerSource } from "DistributedTaskControls/Sources/RepositoryPickerSource";
import {
    IRepositorySelectorState,
    RepositorySelectorStore,
    IRepositoryBrowseState
} from "DistributedTaskControls/Stores/RepositorySelectorStore";
import * as ServiceEndpointContracts from "TFS/ServiceEndpoint/Contracts";
import * as Utils_String from "VSS/Utils/String";

export class RepositorySelectorActionCreator extends ActionCreatorBase {

    constructor(store: RepositorySelectorStore) {
        super();
        this._store = store;
    }

    public initialize(instanceId?: string): void {
        this._actions = ActionsHubManager.GetActionsHub<RepositorySelectorActions>(RepositorySelectorActions, instanceId);
        this._loadableComponentActionsHub = ActionsHubManager.GetActionsHub<LoadableComponentActionsHub>(LoadableComponentActionsHub, instanceId);
        this._repositoryPickerSource = RepositoryPickerSource.instance();
    }

    public static getKey(): string {
        return ActionCreatorKeys.RepositorySelector;
    }

    public updateState(state: IRepositorySelectorState): void {
        this._actions.updateState.invoke(state);
    }

    public updateSearchResults(searchKey: string, props: IRepositorySelectorProps): void {
      this._loadableComponentActionsHub.showLoadingExperience.invoke({});
      const repoPromise: IPromise<ServiceEndpointContracts.ServiceEndpointRequestResult> =
          this._repositoryPickerSource.searchRepositories(props.connectionId, props.getSearchEndpointRequest(searchKey));
      const repos: string[] = [];
      repoPromise.then(
          (serviceEndpointExecutionResult) => {
            if (searchKey === this.getState().searchText) {
              if (Utils_String.equals(serviceEndpointExecutionResult.statusCode, "ok", true)) {
                serviceEndpointExecutionResult.result.forEach(element => {
                  repos.push(JSON.parse(element)[this.REPOSITORY_FULLNAME_KEY]);
                });
                this._actions.updateState.invoke({ items: repos, errorMessage: Utils_String.empty });
              } else if (Utils_String.equals(serviceEndpointExecutionResult.statusCode, "422", true)) {
                this._actions.updateState.invoke(
                  { items: repos, errorMessage: DTCResources.RepositorySelectorSearchFailed });
              } else {
                this._actions.updateState.invoke(
                  { items: repos, errorMessage: serviceEndpointExecutionResult.errorMessage });
              }
              this._loadableComponentActionsHub.hideLoadingExperience.invoke({});
            }
          },
          (error) => {
            const errorText: string = error ? error.message || error : Utils_String.empty;
            if (searchKey === this.getState().searchText) {
              this._actions.updateState.invoke({ errorMessage: errorText});
              this._loadableComponentActionsHub.hideLoadingExperience.invoke({});
            }
          });
    }

    public prefetchUserRepositories(connectionId: string, initialListEndpointRequest: IPromise<ServiceEndpointContracts.ServiceEndpointRequest>, initialList: string[]): void {
      if (initialList && initialList.length > 0) {
        this._actions.updateState.invoke({ errorMessage: Utils_String.empty, initialList: initialList });
        return;
      }

      const repoPromise: IPromise<ServiceEndpointContracts.ServiceEndpointRequestResult>
        = this._repositoryPickerSource.getRepositories(connectionId, initialListEndpointRequest);
      const repos: string[] = [];
      repoPromise.then((serviceEndpointExecutionResult) => {
        if (Utils_String.equals(serviceEndpointExecutionResult.statusCode, "ok", true)
          && serviceEndpointExecutionResult.result
          && serviceEndpointExecutionResult.result.length > 0) {
          serviceEndpointExecutionResult.result.forEach(element => {
            repos.push(JSON.parse(element)[this.REPOSITORY_FULLNAME_KEY]);
          });
          this._actions.updateState.invoke({ errorMessage: Utils_String.empty, initialList: repos });
        }
      });
      // not handling error cases as this is just a prefetch step
    }

    public updateInitialItems(connectionId: string, initialListEndpointRequest: IPromise<ServiceEndpointContracts.ServiceEndpointRequest>): void {
      const state = this.getState();
      if (state.initialList && state.initialList.length > 0) {
        this._actions.updateState.invoke({ items: state.initialList});
        this._loadableComponentActionsHub.hideLoadingExperience.invoke({});
      } else {
        this.setInitialItems(connectionId, initialListEndpointRequest, null);
      }
    }

    public setInitialItems(connectionId: string, initialListEndpointRequest: IPromise<ServiceEndpointContracts.ServiceEndpointRequest>, initialList: string[]): void {
      if (initialList && initialList.length > 0) {
        this._actions.updateState.invoke(
          { items: initialList, errorMessage: Utils_String.empty, initialList: initialList });
        this._loadableComponentActionsHub.hideLoadingExperience.invoke({});
        return;
      }

      this._loadableComponentActionsHub.showLoadingExperience.invoke({});
      const repoPromise: IPromise<ServiceEndpointContracts.ServiceEndpointRequestResult>
        = this._repositoryPickerSource.getRepositories(connectionId, initialListEndpointRequest);
      const repos: string[] = [];
      repoPromise.then((serviceEndpointExecutionResult) => {
        if (Utils_String.equals(serviceEndpointExecutionResult.statusCode, "ok", true)) {
          serviceEndpointExecutionResult.result.forEach(element => {
            repos.push(JSON.parse(element)[this.REPOSITORY_FULLNAME_KEY]);
          });
          this._actions.updateState.invoke(
            { items: repos, errorMessage: Utils_String.empty, initialList: repos });
          this._loadableComponentActionsHub.hideLoadingExperience.invoke({});
        } else {
          this._actions.updateState.invoke(
            { items: repos, errorMessage: serviceEndpointExecutionResult.errorMessage, initialList: repos });
          this._loadableComponentActionsHub.hideLoadingExperience.invoke({});
        }}, (error) => {
          const errorMessage: string = error ? error.message || error : Utils_String.empty;
          this._actions.updateState.invoke({ errorMessage: errorMessage});
          this._loadableComponentActionsHub.hideLoadingExperience.invoke({});
      });
    }

    public showLoadingExperience(): void {
      this._loadableComponentActionsHub.showLoadingExperience.invoke({});
    }

    public hideLoadingExperience(): void {
      this._loadableComponentActionsHub.hideLoadingExperience.invoke({});
    }

    private getState(): IRepositoryBrowseState {
        return this._store.getState();
    }

    private _actions: RepositorySelectorActions;
    private _store: RepositorySelectorStore;
    private _repositoryPickerSource: RepositoryPickerSource;
    private _loadableComponentActionsHub: LoadableComponentActionsHub;
    private readonly REPOSITORY_FULLNAME_KEY: string = "full_name";
}
